/**
 * O domínio da conferência do orçamento no Caso (`TP-051`, T12/T13 — ponto
 * 2 do motor, `US-037`). A seção `*Api` espelha
 * `contracts/openapi/anery-api.v1.json` (schemas `AvaliacaoResponse`,
 * `VeredictoResponse`, `VeredictoDaLinhaResponse`, `MotivoResponse`) —
 * escrita à mão, mesma disciplina de `features/contratos/revisao/api/tipos.ts`.
 *
 * ATENÇÃO: `Ponto`/`Modalidade`/`EstadoDaLinha` são vocabulário do
 * `Anery.Motor` (bounded context PRÓPRIO, sem dependência de
 * `Anery.Domain` — `DI4` do TP-051), DIFERENTE do vocabulário de
 * `Modality` do Caso (`AD`/`ID`, `@anery/api-contracts`). Os valores de
 * enum são o NOME DO MEMBRO C# em inglês (`DI24` do TP-051).
 */

export type ModalidadeDoMotorApi = 'Ad' | 'Id' | 'IdComVentilacao' | 'Pacote';

export const MODALIDADES_DO_MOTOR: readonly ModalidadeDoMotorApi[] = ['Ad', 'Id', 'IdComVentilacao', 'Pacote'];

/** A modalidade do Caso (`AD`/`ID` + ventilação) no vocabulário do motor. `null` quando o Caso ainda não tem. */
export function modalidadeDoMotorDoCaso(modalidade: 'AD' | 'ID' | null, ventilacaoContinua: boolean): ModalidadeDoMotorApi | null {
  if (modalidade === 'AD') return 'Ad';
  if (modalidade === 'ID') return ventilacaoContinua ? 'IdComVentilacao' : 'Id';
  return null;
}

export const ROTULOS_DE_MODALIDADE_DO_MOTOR: Record<ModalidadeDoMotorApi, string> = {
  Ad: 'AD — assistência domiciliar',
  Id: 'ID — internação domiciliar',
  IdComVentilacao: 'ID com ventilação',
  Pacote: 'Pacote',
};

export type EstadoDaLinhaApi = 'Ok' | 'Warning' | 'Blocked' | 'NotCovered';

export type EstadoDaLinha = 'ok' | 'alerta' | 'barrado' | 'sem_cobertura';

export const ROTULOS_DO_ESTADO_DA_LINHA: Record<EstadoDaLinha, string> = {
  ok: 'OK',
  alerta: 'Alerta',
  barrado: 'Barrado',
  sem_cobertura: 'Sem cobertura',
};

const ESTADO_DA_LINHA: Record<EstadoDaLinhaApi, EstadoDaLinha> = {
  Ok: 'ok',
  Warning: 'alerta',
  Blocked: 'barrado',
  NotCovered: 'sem_cobertura',
};

export function mapearEstadoDaLinha(bruto: string): EstadoDaLinha {
  return Object.hasOwn(ESTADO_DA_LINHA, bruto) ? ESTADO_DA_LINHA[bruto as EstadoDaLinhaApi] : 'sem_cobertura';
}

export interface MotivoApi {
  readonly regraContratualId: string | null;
  readonly mensagem: string;
  readonly fonte: string | null;
  /** Na inclusão (verificação 4), o código da linha do pacote que inclui o item (`TP-071` T13). */
  readonly pacote?: string | null;
}

export interface VeredictoDaLinhaApi {
  readonly codigo: string;
  readonly estado: string;
  readonly motivos: readonly MotivoApi[];
}

export interface VeredictoApi {
  readonly linhas: readonly VeredictoDaLinhaApi[];
  readonly alertasDeCabecalho: readonly string[];
}

export interface AvaliacaoApi {
  readonly id: string;
  readonly veredito: VeredictoApi;
  readonly criadoEm: string;
}

export interface Motivo {
  readonly regraContratualId: string | null;
  readonly mensagem: string;
  readonly fonte: string | null;
  readonly pacote: string | null;
}

export interface VeredictoDaLinha {
  readonly codigo: string;
  readonly estado: EstadoDaLinha;
  readonly motivos: readonly Motivo[];
}

export interface Veredicto {
  readonly linhas: readonly VeredictoDaLinha[];
  readonly alertasDeCabecalho: readonly string[];
}

export interface Avaliacao {
  readonly id: string;
  readonly veredicto: Veredicto;
  readonly criadoEm: string;
}

export function mapearAvaliacao(bruto: AvaliacaoApi): Avaliacao {
  return {
    id: bruto.id,
    criadoEm: bruto.criadoEm,
    veredicto: {
      alertasDeCabecalho: bruto.veredito.alertasDeCabecalho,
      linhas: bruto.veredito.linhas.map((linha) => ({
        codigo: linha.codigo,
        estado: mapearEstadoDaLinha(linha.estado),
        motivos: linha.motivos.map((motivo) => ({
          regraContratualId: motivo.regraContratualId,
          mensagem: motivo.mensagem,
          fonte: motivo.fonte,
          pacote: motivo.pacote ?? null,
        })),
      })),
    },
  };
}
